import React, { useMemo, useState } from 'react';
import {
  applyTypography,
  BaseButton,
  Chip,
  Modal,
  useAuth,
  useT,
} from '@repo/common';
import { ReactComponent as ArrowBottomIcon20 } from '@repo/common/assets/icons/20px/icon_20px_arrow-bottom.svg';
import { ReactComponent as ArrowTopIcon20 } from '@repo/common/assets/icons/20px/icon_20px_arrow-top.svg';
import { ReactComponent as ArrowRightIcon16 } from '@repo/common/assets/icons/16px/icon_16px_arrow-right.svg';
import { ReactComponent as MoreVerticalIcon20 } from '@repo/common/assets/icons/20px/icon_20px_option_filled.svg';
import { ReactComponent as PinRightIcon16 } from '@repo/common/assets/icons/16px/icon_16px_pin-right_outline.svg';
import { ReactComponent as EditIcon16 } from '@repo/common/assets/icons/16px/icon_16px_edit_outline.svg';
import { ReactComponent as HiddenIcon16 } from '@repo/common/assets/icons/16px/icon_16px_hidden.svg';
import { ReactComponent as DeleteIcon16 } from '@repo/common/assets/icons/16px/icon_16px_delete_outline.svg';

const PinIcon = ({ isPinned }) => {
  if (!isPinned) return null;

  return (
    <div className='flex items-center justify-center shrink-0 size-[24px] text-text-primary'>
      <PinRightIcon16 className='shrink-0 size-[16px]' />
    </div>
  );
};

const NoticeActionMenu = ({
  isPinned,
  onTogglePin,
  onEdit,
  onToggleVisibility,
  onDelete,
}) => {
  const t = useT();
  const buttonClass =
    'bg-grayscale-gray00 content-stretch flex h-[38px] min-w-0 cursor-pointer items-center justify-between border-0 pl-[12px] pr-[8px] py-[10px] relative rounded-[8px] shrink-0 w-full hover:bg-grayscale-gray01 transition-colors';
  const buttonTextWrapperClass =
    'flex h-auto w-full items-center justify-between';
  const iconSlotClass =
    'ml-[8px] flex size-[16px] items-center justify-center p-[2px] shrink-0 overflow-clip';

  return (
    <div
      className='absolute  ml-[8px] z-[20] w-[220px] bg-grayscale-gray00 border border-grayscale-gray02 rounded-[8px] shadow-popup-04 p-[8px] flex flex-col items-start'
      onClick={(e) => e.stopPropagation()}
    >
      <BaseButton
        type='text'
        size='m'
        htmlType='button'
        hideIcon
        typo={false}
        isStatic
        className={buttonClass}
        textWrapperClass={buttonTextWrapperClass}
        onClick={onTogglePin}
      >
        <span
          className={`${applyTypography('b14Strong')} text-grayscale-gray07`}
          style={{ lineHeight: '1.4' }}
        >
          {isPinned
            ? t('Unpin from top', '상단 고정 해제')
            : t('Pin to top', '상단에 고정')}
        </span>
        <span className={iconSlotClass}>
          <PinRightIcon16 className='size-[12px] shrink-0' />
        </span>
      </BaseButton>
      <BaseButton
        type='text'
        size='m'
        htmlType='button'
        hideIcon
        typo={false}
        isStatic
        className={buttonClass}
        textWrapperClass={buttonTextWrapperClass}
        onClick={onEdit}
      >
        <span
          className={`${applyTypography('b14Strong')} text-grayscale-gray07`}
          style={{ lineHeight: '1.4' }}
        >
          {t('Edit notice', '공지 수정')}
        </span>
        <span className={iconSlotClass}>
          <EditIcon16 className='size-[16px] shrink-0' />
        </span>
      </BaseButton>
      <BaseButton
        type='text'
        size='m'
        htmlType='button'
        hideIcon
        typo={false}
        isStatic
        className={buttonClass}
        textWrapperClass={buttonTextWrapperClass}
        onClick={onToggleVisibility}
      >
        <span
          className={`${applyTypography('b14Strong')} text-grayscale-gray07`}
          style={{ lineHeight: '1.4' }}
        >
          {t('Hide (from user)', '숨기기 (사용자에게 비공개)')}
        </span>
        <span className={iconSlotClass}>
          <HiddenIcon16 className='size-[12px] shrink-0' />
        </span>
      </BaseButton>
      <BaseButton
        type='text'
        size='m'
        htmlType='button'
        hideIcon
        typo={false}
        isStatic
        className={buttonClass}
        textWrapperClass={buttonTextWrapperClass}
        onClick={onDelete}
      >
        <span
          className={`${applyTypography('b14Strong')} text-grayscale-gray07`}
          style={{ lineHeight: '1.4' }}
        >
          {t('Delete', '삭제')}
        </span>
        <span className={iconSlotClass}>
          <DeleteIcon16 className='size-[16px] shrink-0' />
        </span>
      </BaseButton>
    </div>
  );
};

const NoticeItem = ({
  title,
  date,
  content,
  status = 'ACTIVE',
  isPinned = false,
  isExpanded = false,
  onToggle,
  isAdmin = false,
  isMenuOpen = false,
  onMenuOpen,
  onEdit,
  onToggleVisibility,
  onDelete,
  onTogglePin,
}) => {
  const t = useT();
  const normalizedStatus = String(status || '').toUpperCase();
  const isHidden =
    normalizedStatus === 'HIDDEN' || normalizedStatus === 'INACTIVE';

  return (
    <div
      className={`relative flex w-full border-b border-solid ${
        isHidden
          ? 'bg-grayscale-gray02 border-grayscale-gray02'
          : 'bg-transparent border-grayscale-gray02'
      }`}
    >
      {isExpanded ? (
        <div className='flex flex-col items-start w-full'>
          <div className='flex h-[90px] items-center px-[24px] w-full'>
            <div className='flex flex-1 items-center gap-[8px] pr-[24px] min-w-0'>
              <PinIcon isPinned={isPinned} />
              {isHidden && (
                <Chip
                  type='fill'
                  hideIcon
                  disableTypography
                  disableDefaultColors
                  bgclass='bg-grayscale-gray02 rounded-[8px] px-[12px] py-[6px] h-auto pointer-events-none'
                  hoverclass='hover:opacity-100 hover:bg-grayscale-gray02'
                  activeclass='active:opacity-100 active:bg-grayscale-gray02'
                  borderclass='ring-0'
                  aria-label={t('Hidden', '숨김')}
                >
                  <span
                    className={`${applyTypography(
                      'b13Strong',
                    )} text-grayscale-gray07 leading-[1.4]`}
                  >
                    Hidden
                  </span>
                </Chip>
              )}
              <p
                className={`${applyTypography(
                  'b16Strong',
                )} text-text-primary flex-1 min-h-px min-w-px overflow-hidden text-ellipsis whitespace-pre-wrap`}
                style={{ lineHeight: '1.5' }}
              >
                {title}
              </p>
            </div>
            <div className='relative flex items-center shrink-0 h-full gap-[8px]'>
              <div className='flex items-center justify-center h-full pr-[16px] relative shrink-0'>
                <p
                  className={`${applyTypography(
                    'b14',
                  )} text-text-placeholder shrink-0 text-center`}
                  style={{ lineHeight: '1.4' }}
                >
                  {date}
                </p>
              </div>
              <BaseButton
                type='text'
                size='s'
                className='min-w-0 rounded-none border-0 bg-transparent p-0 hover:bg-transparent relative shrink-0 size-[30px] cursor-pointer hover:opacity-70 transition-opacity'
                onClick={onToggle}
                aria-label={t('Collapse notice', '공지 접기')}
                icon={
                  <ArrowTopIcon20 className='overflow-clip relative shrink-0 size-[20px]' />
                }
              />
              {isAdmin && (
                <div className='relative'>
                  <BaseButton
                    type='text'
                    size='s'
                    className='min-w-0 rounded-none border-0 bg-transparent p-0 hover:bg-transparent relative shrink-0 size-[30px] cursor-pointer hover:opacity-70 transition-opacity'
                    onClick={onMenuOpen}
                    aria-label={t('Open notice options', '공지 옵션 열기')}
                    icon={
                      <MoreVerticalIcon20 className='overflow-clip relative shrink-0 size-[20px]' />
                    }
                  />
                  {isMenuOpen && (
                    <NoticeActionMenu
                      isPinned={isPinned}
                      onTogglePin={onTogglePin}
                      onEdit={onEdit}
                      onToggleVisibility={onToggleVisibility}
                      onDelete={onDelete}
                    />
                  )}
                </div>
              )}
            </div>
          </div>
          <div className='bg-grayscale-gray01 overflow-clip px-[32px] py-[32px] w-full'>
            <p
              className={`${applyTypography(
                'b14',
              )} text-text-primary whitespace-pre-wrap`}
              style={{ lineHeight: '1.4' }}
            >
              {content}
            </p>
          </div>
        </div>
      ) : (
        <div className='flex h-[90px] items-center px-[24px] w-full'>
          <div className='flex items-center gap-[8px] pr-[24px] flex-1 min-w-0'>
            <PinIcon isPinned={isPinned} />
            {isHidden && (
              <Chip
                type='fill'
                hideIcon
                disableTypography
                disableDefaultColors
                bgclass='bg-grayscale-gray02 rounded-[8px] px-[12px] py-[6px] h-auto pointer-events-none'
                hoverclass='hover:opacity-100 hover:bg-grayscale-gray02'
                activeclass='active:opacity-100 active:bg-grayscale-gray02'
                borderclass='ring-0'
                aria-label={t('Hidden', '숨김')}
              >
                <span
                  className={`${applyTypography(
                    'b13Strong',
                  )} text-grayscale-gray07 leading-[1.4]`}
                >
                  {t('Hidden', '숨김')}
                </span>
              </Chip>
            )}
            <p
              className={`${applyTypography(
                'b16Strong',
              )} text-text-primary flex-1 min-h-px min-w-px overflow-hidden text-ellipsis whitespace-pre-wrap`}
              style={{ lineHeight: '1.5' }}
            >
              {title}
            </p>
          </div>
          <div className='relative flex items-center shrink-0 h-full gap-[8px]'>
            <div className='flex items-center justify-center h-full pr-[16px] relative shrink-0'>
              <p
                className={`${applyTypography(
                  'b14',
                )} text-text-placeholder shrink-0 text-center`}
                style={{ lineHeight: '1.4' }}
              >
                {date}
              </p>
            </div>
            <BaseButton
              type='text'
              size='s'
              className='min-w-0 rounded-none border-0 bg-transparent p-0 hover:bg-transparent relative shrink-0 size-[30px] cursor-pointer hover:opacity-70 transition-opacity'
              onClick={onToggle}
              aria-label={t('Expand notice', '공지 펼치기')}
              icon={
                <ArrowBottomIcon20 className='overflow-clip relative shrink-0 size-[20px]' />
              }
            />
            {isAdmin && (
              <div className='relative'>
                <BaseButton
                  type='text'
                  size='s'
                  className='min-w-0 rounded-none border-0 bg-transparent p-0 hover:bg-transparent relative shrink-0 size-[30px] cursor-pointer hover:opacity-70 transition-opacity'
                  onClick={onMenuOpen}
                  aria-label={t('Open notice options', '공지 옵션 열기')}
                  icon={
                    <MoreVerticalIcon20 className='overflow-clip relative shrink-0 size-[20px]' />
                  }
                />
                {isMenuOpen && (
                  <NoticeActionMenu
                    isPinned={isPinned}
                    onTogglePin={onTogglePin}
                    onEdit={onEdit}
                    onToggleVisibility={onToggleVisibility}
                    onDelete={onDelete}
                  />
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const Pagination = ({ currentPage = 1, totalPages = 10, onPageChange }) => {
  const t = useT();
  const handlePrev = () => {
    if (currentPage > 1) {
      onPageChange(currentPage - 1);
    }
  };

  const handleNext = () => {
    if (currentPage < totalPages) {
      onPageChange(currentPage + 1);
    }
  };

  const handlePageClick = (page) => {
    onPageChange(page);
  };

  return (
    <div className='flex gap-[12px] items-center justify-center relative shrink-0 w-full'>
      <BaseButton
        type='text'
        size='s'
        onClick={handlePrev}
        isDisabled={currentPage === 1}
        className='h-auto min-w-0 border-0 bg-transparent hover:bg-transparent p-[8px] relative rounded-[8px] shrink-0 disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-70 transition-opacity'
        aria-label={t('Previous page', '이전 페이지')}
        icon={
          <div className='overflow-clip relative shrink-0 size-[16px] rotate-180'>
            <ArrowRightIcon16 className='w-full h-full' />
          </div>
        }
      />

      <div className='flex gap-[6px] items-center justify-center relative shrink-0'>
        {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
          <BaseButton
            key={page}
            type='text'
            size='s'
            typo={false}
            hideIcon
            isStatic
            onClick={() => handlePageClick(page)}
            className={`min-w-0 cursor-pointer border-0 p-[6px] relative rounded-[8px] shrink-0 size-[32px] transition-colors ${
              currentPage === page
                ? 'bg-green-deep02'
                : 'bg-transparent hover:bg-grayscale-gray01'
            }`}
            textWrapperClass='h-auto'
            aria-current={currentPage === page ? 'page' : undefined}
          >
            <p
              className={`${applyTypography(
                'b13Strong',
              )} shrink-0 text-center ${
                currentPage === page
                  ? 'text-text-inverse'
                  : 'text-text-secondary'
              }`}
              style={{ lineHeight: '1.4' }}
            >
              {page}
            </p>
          </BaseButton>
        ))}
      </div>

      <BaseButton
        type='text'
        size='s'
        onClick={handleNext}
        isDisabled={currentPage === totalPages}
        className='h-auto min-w-0 border-0 bg-transparent hover:bg-transparent p-[8px] relative rounded-[8px] shrink-0 disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-70 transition-opacity'
        aria-label={t('Next page', '다음 페이지')}
        icon={
          <ArrowRightIcon16 className='overflow-clip relative shrink-0 size-[16px]' />
        }
      />
    </div>
  );
};

const NoticeSection = ({
  notices = [],
  onCreateNotice,
  onEditNotice,
  onToggleNoticeVisibility,
  onDeleteNotice,
  onToggleNoticePin,
}) => {
  const t = useT();
  const { user } = useAuth();
  const [expandedNoticeKeys, setExpandedNoticeKeys] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const [menuOpenNoticeKey, setMenuOpenNoticeKey] = useState(null);
  const [hideConfirmNotice, setHideConfirmNotice] = useState(null);
  const [deleteConfirmNotice, setDeleteConfirmNotice] = useState(null);
  const normalizedRole = String(user?.role ?? '').toUpperCase();
  const isAdmin =
    normalizedRole === 'A' ||
    normalizedRole === 'ROLE_A' ||
    normalizedRole === 'ADMIN' ||
    normalizedRole === 'ROLE_ADMIN';

  const noticesWithMeta = useMemo(() => {
    const source = Array.isArray(notices) ? notices : [];
    return source.map((notice, index) => {
      const id = notice?.id ?? notice?.noticeId ?? notice?.workshopNoticeId;
      const noticeKey =
        id !== undefined && id !== null && String(id).trim() !== ''
          ? `id:${id}`
          : `idx:${index}|title:${String(notice?.title ?? '')}|date:${String(
              notice?.date ?? '',
            )}`;

      return {
        ...notice,
        __sourceIndex: index,
        __noticeKey: noticeKey,
      };
    });
  }, [notices]);

  const sortedNotices = useMemo(() => {
    return [...noticesWithMeta].sort((a, b) => {
      if (a.isPinned === b.isPinned) {
        return a.__sourceIndex - b.__sourceIndex;
      }
      return a.isPinned ? -1 : 1;
    });
  }, [noticesWithMeta]);

  const handleToggle = (noticeKey) => {
    setExpandedNoticeKeys((prev) =>
      prev.includes(noticeKey)
        ? prev.filter((key) => key !== noticeKey)
        : [...prev, noticeKey],
    );
  };

  const handleMenuOpen = (noticeKey) => {
    setMenuOpenNoticeKey((prev) => (prev === noticeKey ? null : noticeKey));
  };

  const handleEdit = (noticeItem) => {
    const target = notices[noticeItem.__sourceIndex] ?? noticeItem;
    onEditNotice?.(target, noticeItem.__sourceIndex);
    setMenuOpenNoticeKey(null);
  };

  const handleToggleVisible = (noticeItem) => {
    const currentStatus = String(
      noticeItem?.status ?? noticeItem?.noticeStatus ?? 'ACTIVE',
    ).toUpperCase();
    const isCurrentlyHidden =
      currentStatus === 'HIDDEN' || currentStatus === 'INACTIVE';
    if (!isCurrentlyHidden) {
      setHideConfirmNotice(noticeItem);
      setMenuOpenNoticeKey(null);
      return;
    }

    const target = notices[noticeItem.__sourceIndex] ?? noticeItem;
    onToggleNoticeVisibility?.(target, noticeItem.__sourceIndex);
    setMenuOpenNoticeKey(null);
  };

  const handleDelete = (noticeItem) => {
    setDeleteConfirmNotice(noticeItem);
    setMenuOpenNoticeKey(null);
  };

  const handleConfirmHide = () => {
    if (!hideConfirmNotice) return;
    const target =
      notices[hideConfirmNotice.__sourceIndex] ?? hideConfirmNotice;
    onToggleNoticeVisibility?.(target, hideConfirmNotice.__sourceIndex);
    setHideConfirmNotice(null);
  };

  const handleConfirmDelete = () => {
    if (!deleteConfirmNotice) return;
    const target =
      notices[deleteConfirmNotice.__sourceIndex] ?? deleteConfirmNotice;
    onDeleteNotice?.(target, deleteConfirmNotice.__sourceIndex);
    setDeleteConfirmNotice(null);
  };

  const handleTogglePin = (noticeItem) => {
    const target = notices[noticeItem.__sourceIndex] ?? noticeItem;
    const nextPinned = !Boolean(noticeItem.isPinned);
    onToggleNoticePin?.(target, noticeItem.__sourceIndex, nextPinned);
    setMenuOpenNoticeKey(null);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    setExpandedNoticeKeys([]);
  };

  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentNotices = sortedNotices.slice(startIndex, endIndex);
  const totalPages = Math.ceil(sortedNotices.length / itemsPerPage);
  const hasNotices = currentNotices.length > 0;

  return (
    <div className='flex flex-col items-start justify-start w-full min-h-[700px] max-w-[1462px] mx-auto'>
      <div className='flex flex-col items-center justify-center w-full'>
        {isAdmin && hasNotices && (
          <div className='flex justify-end w-full pb-[24px]'>
            <BaseButton
              type='text'
              size='m'
              hideIcon
              onClick={() => onCreateNotice?.()}
              aria-label={t('Create notice', '공지 작성')}
            >
              {t('Create a notice', '공지 작성하기')}
            </BaseButton>
          </div>
        )}
        <div className='flex flex-col items-start pb-[40px] w-full h-full'>
          {hasNotices ? (
            currentNotices.map((notice) => {
              const noticeKey = notice.__noticeKey;
              return (
                <NoticeItem
                  key={noticeKey}
                  title={notice.title}
                  date={notice.date}
                  content={notice.content}
                  status={notice.status ?? notice.noticeStatus}
                  isPinned={notice.isPinned}
                  isExpanded={expandedNoticeKeys.includes(noticeKey)}
                  onToggle={() => handleToggle(noticeKey)}
                  isAdmin={isAdmin}
                  isMenuOpen={menuOpenNoticeKey === noticeKey}
                  onMenuOpen={() => handleMenuOpen(noticeKey)}
                  onEdit={() => handleEdit(notice)}
                  onToggleVisibility={() => handleToggleVisible(notice)}
                  onDelete={() => handleDelete(notice)}
                  onTogglePin={() => handleTogglePin(notice)}
                />
              );
            })
          ) : (
            <div className='w-full min-h-[620px] flex items-center justify-center'>
              {!isAdmin && (
                <p
                  className={`${applyTypography('b14')} text-text-placeholder`}
                >
                  등록된 공지사항이 없습니다.
                </p>
              )}
              {isAdmin && (
                <BaseButton
                  type='text'
                  size='m'
                  hideIcon
                  onClick={() => onCreateNotice?.()}
                  aria-label={t('Create notice', '공지 작성')}
                >
                  Create a notice
                </BaseButton>
              )}
            </div>
          )}
        </div>

        {totalPages > 1 && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
          />
        )}
      </div>

      <Modal
        isOpen={Boolean(hideConfirmNotice)}
        type='warning'
        title={t('Hide this notice?', '이 공지를 숨길까요?')}
        subText={t("This action can't be undone", '이 작업은 되돌릴 수 없습니다')}
        leftButtonLabel={t('cancel', '취소')}
        rightButtonLabel={t('Hide', '숨기기')}
        onClickLeftBtn={() => setHideConfirmNotice(null)}
        onClickRightBtn={handleConfirmHide}
      />

      <Modal
        isOpen={Boolean(deleteConfirmNotice)}
        type='warning'
        title={t('Delete this notice?', '이 공지를 삭제할까요?')}
        subText={t("This action can't be undone", '이 작업은 되돌릴 수 없습니다')}
        leftButtonLabel={t('cancel', '취소')}
        rightButtonLabel={t('Delete', '삭제')}
        onClickLeftBtn={() => setDeleteConfirmNotice(null)}
        onClickRightBtn={handleConfirmDelete}
      />
    </div>
  );
};

export default NoticeSection;
